import type { TokenResponse, VkMiniAppsAuthRequest, YandexGamesAuthRequest } from '@uznali/api-client';

import { sessionMetadataSchema, sessionTokensSchema, tokenStore } from '@shared/lib/auth/token-store';

import { apiClient } from './client';

const saveSession = (response: unknown, expected: TokenResponse | null) => {
  const current = tokenStore.getForRequest();
  if (current?.accessToken !== expected?.accessToken || current?.refreshToken !== expected?.refreshToken)
    throw new Error('Auth session changed');
  const metadata = sessionMetadataSchema.parse(response);
  const tokens = sessionTokensSchema.parse(response);
  tokenStore.setTemporary(null);
  tokenStore.set({ ...tokens, ...metadata }, true);
  return metadata;
};

let guestRequest: Promise<void> | null = null;

export const authSessionApi = {
  async read() {
    const tokens = tokenStore.getForRequest();
    if (!tokens) return null;
    const metadata = sessionMetadataSchema.parse(await apiClient.auth.getSession());
    // /me contains empty token fields. Only metadata belongs in the token store.
    return tokenStore.updateMetadata(metadata, tokens) ? metadata : null;
  },
  async guest() {
    const expected = tokenStore.getForRequest();
    return saveSession(await apiClient.auth.guestSignIn({ platform: 'web' }), expected);
  },
  async ensureGuest() {
    if (tokenStore.getForRequest()) return;
    const createIfMissing = async () => {
      if (tokenStore.getForRequest()) return;
      await this.guest();
    };
    guestRequest ??= (async () => {
      if (typeof navigator !== 'undefined' && navigator.locks)
        await navigator.locks.request('uznali:guest-session', createIfMissing);
      else await createIfMissing();
    })().finally(() => {
      guestRequest = null;
    });
    await guestRequest;
  },
  async vkMiniApps(payload: VkMiniAppsAuthRequest) {
    const expected = tokenStore.getForRequest();
    return saveSession(await apiClient.auth.vkMiniAppsSignIn(payload), expected);
  },
  async yandexGames(payload: YandexGamesAuthRequest) {
    const expected = tokenStore.getForRequest();
    return saveSession(await apiClient.auth.yandexGamesSignIn(payload), expected);
  },
};
